import { ResultDetails } from '@/components/ui/ResultDetails';
import { ApiError } from '@/lib/api/client';
import React, { useEffect, useState, type CSSProperties } from 'react';
import {
  uploadWorkRequestFile,
  downloadWorkRequestFile,
  answerClarification,
  assignWorkRequest,
  cancelWorkRequest,
  confirmWorkRequestResult,
  getWorkRequestDetail,
  requestClarification,
  submitWorkRequestResult,
  updateInternalNote,
  updateWorkRequestStatus,
} from '../api';
import type { WorkRequestDetail } from '../types';
import { ServiceExecutionPanel } from './ServiceExecutionPanel';

export type PassportTab =
  | 'overview'
  | 'details'
  | 'answers'
  | 'files'
  | 'clarifications'
  | 'execution'
  | 'timeline';

interface RequestPassportModalProps {
  isOpen: boolean;
  requestIdOrCode: string | null;
  initialTab?: PassportTab;
  onClose: () => void;
  onUpdated?: (updated: WorkRequestDetail) => void;
  roleContext?: string;
}

const STATUS_COLOR: Record<string, string> = {
  IN_PROGRESS: '#49ddb7',
  ACTIVE: '#49ddb7',
  NEEDS_CLARIFICATION: '#e2ad3a',
  NOT_STARTED: '#66b7e8',
  NEW: '#66b7e8',
  COMPLETED: '#41d2ff',
  RESULT_READY: '#e2ad3a',
  DONE: '#41d2ff',
  CANCELLED: '#ff7b65',
  DRAFT: '#9e80ff',
};

function priorityLabel(code?: string | null): string {
  if (!code) return '—';
  const stripped = code.replace(/\s*\([A-Z_]+\)\s*$/, '').trim();
  const map: Record<string, string> = {
    LOW: 'Низкий',
    MEDIUM: 'Средний',
    STANDARD: 'Обычный',
    HIGH: 'Высокий',
    URGENT: 'Срочный',
  };
  return map[code] || map[stripped] || stripped;
}

function roleLabel(code?: string | null): string {
  if (!code) return '—';
  const map: Record<string, string> = {
    DOCTORAL: 'Докторант',
    LEARNER: 'Докторант',
    AUTHOR: 'Автор / соавтор',
    CLIENT: 'Клиент',
    TEACHER: 'Преподаватель',
    ADVISOR: 'Эдвайзер',
    SUPERVISOR: 'Научный руководитель',
    SYSTEM_OWNER: 'Владелец системы',
    OPERATIONS_PARTNER: 'Операционный партнёр',
    RESEARCHER: 'Исследователь',
  };
  return map[code] || code;
}

function kindLabel(code?: string | null): string {
  if (!code) return '—';
  const map: Record<string, string> = {
    SERVICE: 'Сервис / услуга',
    ARTICLE: 'Научная статья',
    GRANT: 'Грант',
    EVENT: 'Конференция',
    PROJECT: 'Исследовательский проект',
    OTHER: 'Другое',
  };
  return map[code] || code;
}

function fileStatusLabel(code?: string | null): string {
  if (!code) return '—';
  const map: Record<string, string> = {
    UPLOADED: 'Загружен',
    VERIFIED: 'Проверен',
    REJECTED: 'Отклонён',
    DRAFT: 'Черновик',
  };
  return map[code] || code;
}

function answerStatusLabel(code?: string | null): string {
  if (!code) return '';
  const map: Record<string, string> = {
    SUBMITTED: 'Отправлен',
    DRAFT: 'Черновик',
    VERIFIED: 'Проверен',
  };
  return map[code] || code;
}

export const RequestPassportModal: React.FC<RequestPassportModalProps> = ({
  isOpen,
  requestIdOrCode,
  initialTab = 'overview',
  onClose,
  onUpdated,
  roleContext,
}) => {
  const [request, setRequest] = useState<WorkRequestDetail | null>(null);
  const [activeTab, setActiveTab] = useState<PassportTab>(initialTab);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [newStatus, setNewStatus] = useState<string>('');
  const [statusReason, setStatusReason] = useState<string>('');
  const [newAssignee, setNewAssignee] = useState<string>('');
  const [internalNotesText, setInternalNotesText] = useState<string>('');
  const [internalNotesSaved, setInternalNotesSaved] = useState<boolean>(false);

  const [clarificationQuestion, setClarificationQuestion] = useState<string>('');
  const [clarificationDeadline, setClarificationDeadline] = useState<string>('');
  const [clarificationSubmitting, setClarificationSubmitting] = useState<boolean>(false);
  const [showClarificationForm, setShowClarificationForm] = useState<boolean>(false);

  const [activeClarificationId, setActiveClarificationId] = useState<string | null>(null);
  const [clarificationAnswerText, setClarificationAnswerText] = useState<string>('');
  const [answeringSubmitting, setAnsweringSubmitting] = useState<boolean>(false);

  const [selectedMaterialFile, setSelectedMaterialFile] = useState<File | null>(null);
  const [newFileVersion, setNewFileVersion] = useState<string>('v1');
  const [fileSubmitting, setFileSubmitting] = useState<boolean>(false);
  const [showFileForm, setShowFileForm] = useState<boolean>(false);

  const [resultSummary, setResultSummary] = useState<string>('');
  const [selectedResultFiles, setSelectedResultFiles] = useState<File[]>([]);
  const [resultSubmitting, setResultSubmitting] = useState<boolean>(false);
  const [showResultForm, setShowResultForm] = useState<boolean>(false);

  const [actionLoading, setActionLoading] = useState<boolean>(false);

  useEffect(() => {
    if (initialTab) {
      setActiveTab(initialTab);
    }
  }, [initialTab]);

  const loadData = async () => {
    if (!requestIdOrCode) return;
    try {
      setLoading(true);
      setError(null);
      const detail = await getWorkRequestDetail(requestIdOrCode, roleContext);
      setRequest(detail);
      setNewStatus(detail.status);
      setNewAssignee(detail.assignee || '');
      setInternalNotesText(detail.internal_notes || '');
    } catch {
      setError('Не удалось загрузить данные заявки');
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (isOpen && requestIdOrCode) {
      loadData();
    } else {
      setRequest(null);
    }
  }, [isOpen, requestIdOrCode]);

  if (!isOpen || !requestIdOrCode) return null;

  const handleUpdateStatus = async () => {
    if (!request || !newStatus) return;
    setError(null);
    try {
      setActionLoading(true);
      const updated = await updateWorkRequestStatus(
        request.id,
        newStatus,
        statusReason.trim() || undefined,
        roleContext
      );
      setRequest(updated);
      setStatusReason('');
      if (onUpdated) onUpdated(updated);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Не удалось сменить статус. Обновите заявку и повторите попытку.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleAssign = async () => {
    if (!request || !newAssignee.trim()) return;
    try {
      setActionLoading(true);
      const updated = await assignWorkRequest(request.id, newAssignee.trim(), undefined, roleContext);
      setRequest(updated);
      if (onUpdated) onUpdated(updated);
    } catch (reason) {
      setError(reason instanceof Error ? reason.message : 'Ошибка при назначении исполнителя');
    } finally {
      setActionLoading(false);
    }
  };

  const handleSaveInternalNotes = async () => {
    if (!request) return;
    try {
      setActionLoading(true);
      const updated = await updateInternalNote(request.id, internalNotesText, roleContext);
      setRequest(updated);
      setInternalNotesSaved(true);
      setTimeout(() => setInternalNotesSaved(false), 2000);
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка сохранения внутренних заметок');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCreateClarification = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || !clarificationQuestion.trim()) return;
    try {
      setClarificationSubmitting(true);
      const updated = await requestClarification(
        request.id,
        {
          question: clarificationQuestion.trim(),
          deadline: clarificationDeadline.trim() || undefined,
        },
        roleContext
      );
      setRequest(updated);
      setClarificationQuestion('');
      setClarificationDeadline('');
      setShowClarificationForm(false);
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка при создании запроса на уточнение');
    } finally {
      setClarificationSubmitting(false);
    }
  };

  const handleAnswerClarification = async (clarificationId: string) => {
    if (!request || !clarificationAnswerText.trim()) return;
    try {
      setAnsweringSubmitting(true);
      const updated = await answerClarification(
        request.id,
        clarificationId,
        {
          answer: clarificationAnswerText.trim(),
        },
        roleContext
      );
      setRequest(updated);
      setActiveClarificationId(null);
      setClarificationAnswerText('');
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка при отправке ответа на уточнение');
    } finally {
      setAnsweringSubmitting(false);
    }
  };

  const handleAddFile = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || !selectedMaterialFile) return;
    try {
      setFileSubmitting(true);
      const updated = await uploadWorkRequestFile(request.id, selectedMaterialFile, newFileVersion.trim() || 'v1', roleContext);
      setRequest(updated);
      setSelectedMaterialFile(null);
      setShowFileForm(false);
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка добавления файла');
    } finally {
      setFileSubmitting(false);
    }
  };

  const handleSubmitResult = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!request || !resultSummary.trim()) return;
    try {
      setResultSubmitting(true);
      const files = [];
      for (const file of selectedResultFiles) {
        const uploaded = await uploadWorkRequestFile(request.id, file, 'result', roleContext);
        setRequest(uploaded);
        files.push(uploaded.files[uploaded.files.length - 1]);
      }
      const updated = await submitWorkRequestResult(
        request.id,
        {
          summary: resultSummary.trim(),
          files,
        },
        roleContext
      );
      setRequest(updated);
      setShowResultForm(false);
      setResultSummary('');
      setSelectedResultFiles([]);
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка при фиксации результата');
    } finally {
      setResultSubmitting(false);
    }
  };

  const handleConfirmResult = async () => {
    if (!request) return;
    setError(null);
    try {
      setActionLoading(true);
      const updated = await confirmWorkRequestResult(request.id, roleContext);
      setRequest(updated);
      if (onUpdated) onUpdated(updated);
    } catch (reason) {
      setError(reason instanceof ApiError ? reason.message : 'Не удалось подтвердить получение. Обновите заявку и повторите попытку.');
    } finally {
      setActionLoading(false);
    }
  };

  const handleCancelRequest = async () => {
    if (!request) return;
    if (!window.confirm('Вы действительно хотите отменить заявку?')) return;
    try {
      setActionLoading(true);
      const updated = await cancelWorkRequest(request.id, 'Отменено пользователем', roleContext);
      setRequest(updated);
      if (onUpdated) onUpdated(updated);
    } catch {
      setError('Ошибка отмены заявки');
    } finally {
      setActionLoading(false);
    }
  };

  const getStatusBadge = (status: string, label: string) => (
    <span
      className="owner-uat-badge"
      style={{ '--badge': STATUS_COLOR[status] || '#8faebb' } as CSSProperties}
    >
      {label}
    </span>
  );

  const tabBtn = (tab: PassportTab, label: string, count?: number, countWarn?: boolean) => (
    <button
      type="button"
      onClick={() => setActiveTab(tab)}
      className={`owner-uat-filter${activeTab === tab ? ' active' : ''}`}
    >
      {label}
      {typeof count === 'number' && count > 0 ? (
        <span
          className="owner-uat-badge"
          style={
            {
              '--badge': countWarn ? '#e2ad3a' : '#8faebb',
              marginLeft: 6,
            } as CSSProperties
          }
        >
          {count}
        </span>
      ) : null}
    </button>
  );

  return (
    <div className="owner-uat-modal-backdrop" role="dialog" aria-modal="true">
      <div className="owner-uat-modal owner-uat-modal--passport">
        <header className="owner-uat-modal-head">
          <div>
            <small className="owner-uat-eyebrow">
              {request ? request.reference_code : requestIdOrCode}
            </small>
            <h2>
              {request?.title || request?.service_name || 'Паспорт заявки'}
              {request ? (
                <span style={{ marginLeft: 10, verticalAlign: 'middle' }}>
                  {getStatusBadge(request.status, request.status_label)}
                </span>
              ) : null}
            </h2>
            <p>
              Заказчик: {request?.requester_name || '—'} · Направление: {request?.direction || '—'} ·
              Приоритет: {priorityLabel(request?.priority)}
            </p>
          </div>
          <button type="button" className="owner-uat-btn" onClick={onClose} aria-label="Закрыть">
            ✕
          </button>
        </header>

        <nav className="owner-uat-filters owner-uat-modal-tabs">
          {tabBtn('overview', 'Обзор')}
          {tabBtn('details', 'Сведения и материалы')}
          {tabBtn('answers', 'Все ответы', request?.answers?.length)}
          {tabBtn('files', 'Файлы', request?.files?.length)}
          {request?.service_id && ['FIND_SCIENTIFIC_MATERIALS', 'VERIFY_REFERENCES_DOI', 'EXTRACT_INFORMATION_FROM_FILES', 'CREATE_RESEARCH_DATABASE', 'CREATE_LANGUAGE_CORPUS', 'PROCESS_FILES', 'DESIGN_RESEARCH', 'CREATE_RESEARCH_INSTRUMENT', 'ANALYZE_RESEARCH_DATA', 'SYSTEMATIC_REVIEW'].includes(request.service_id) && tabBtn('execution', 'Выполнение услуги')}
          {tabBtn(
            'clarifications',
            'Уточнения',
            request?.clarifications?.length,
            request?.clarifications?.some((c) => c.status === 'PENDING')
          )}
          {tabBtn('timeline', 'История')}
        </nav>

        <div className="owner-uat-modal-body">
          {loading && <div className="owner-uat-note">Загрузка паспорта заявки...</div>}

          {error && (
            <div className="owner-uat-note danger" style={{ marginBottom: 12 }}>
              <span>{error}</span>
              <button type="button" className="owner-uat-btn" onClick={() => setError(null)} style={{ marginLeft: 10 }}>
                Закрыть
              </button>
            </div>
          )}

          {!loading && request && (
            <>
              {activeTab === 'execution' && <ServiceExecutionPanel request={request} roleContext={roleContext} onUpdated={(updated) => { setRequest(updated); onUpdated?.(updated); }} />}
              {activeTab === 'overview' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-kpis">
                    <article className="owner-uat-kpi" style={{ '--kpi': '#41d2ff' } as CSSProperties}>
                      <small>Этап исполнения</small>
                      <strong style={{ fontSize: 16 }}>{request.stage || 'Инициация'}</strong>
                    </article>
                    <article className="owner-uat-kpi" style={{ '--kpi': '#49ddb7' } as CSSProperties}>
                      <small>Исполнитель</small>
                      <strong style={{ fontSize: 16 }}>{request.assignee || 'Не назначен'}</strong>
                    </article>
                    <article className="owner-uat-kpi" style={{ '--kpi': '#66b7e8' } as CSSProperties}>
                      <small>Плановый срок</small>
                      <strong style={{ fontSize: 16 }}>{request.due_at_label || 'Не установлен'}</strong>
                    </article>
                    <article className="owner-uat-kpi" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
                      <small>Приоритет</small>
                      <strong style={{ fontSize: 16, color: '#e2ad3a' }}>
                        {priorityLabel(request.priority)}
                      </strong>
                    </article>
                  </div>

                  {request.blocking_reason && (
                    <div className="owner-uat-note">
                      <strong>Причина блокировки / Требуется уточнение</strong>
                      <div style={{ marginTop: 4 }}>{request.blocking_reason}</div>
                    </div>
                  )}

                  {request.result && (
                    <div className="owner-uat-panel" style={{ '--kpi': '#49ddb7' } as CSSProperties}>
                      <header>
                        <div>
                          <h3>
                            <span
                              className="owner-uat-badge"
                              style={{ '--badge': '#49ddb7' } as CSSProperties}
                            >
                              {request.result.is_confirmed_by_client ? 'Принятый результат' : request.status === 'RESULT_READY' ? 'Готовый результат' : 'Ранее переданный результат'}
                            </span>
                          </h3>
                          <p>
                            Подготовлен: {request.result.prepared_by} ({request.result.ready_at})
                          </p>
                        </div>
                        <div className="owner-uat-spacer" />
                        {request.result.is_confirmed_by_client ? (
                          <span
                            className="owner-uat-badge"
                            style={{ '--badge': '#49ddb7' } as CSSProperties}
                          >
                            ✓ Подтверждён заказчиком ({request.result.confirmed_at})
                          </span>
                        ) : request.can_confirm_result && request.status === 'RESULT_READY' ? (
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={handleConfirmResult}
                            className="owner-uat-btn primary"
                          >
                            Подтвердить получение результата
                          </button>
                        ) : (
                          <span style={{ color: '#e2ad3a', fontSize: 12 }}>
                            {request.status === 'RESULT_READY' ? 'Ожидает подтверждения заказчиком' : 'Приёмка недоступна до повторной передачи результата исполнителем'}
                          </span>
                        )}
                      </header>
                      <div className="owner-uat-panel-body">
                        <p style={{ margin: 0 }}>{request.result.summary}</p>
                        {request.result.files && request.result.files.length > 0 && (
                          <div className="owner-uat-actions" style={{ marginTop: 10 }}>
                            {request.result.files.map((rf, idx) => (
                              <span
                                key={idx}
                                className="owner-uat-badge"
                                style={{ '--badge': '#8faebb' } as CSSProperties}
                              >
                                📎 {rf.filename} {rf.filesize_label || ''}
                                {rf.url && <button type="button" onClick={() => void downloadWorkRequestFile(rf.url!, rf.filename, roleContext).catch((error) => setError(error.message))}>Скачать</button>}
                              </span>
                            ))}
                          </div>
                        )}
                      </div>
                    </div>
                  )}

                  <section className="owner-uat-panel">
                    <header>
                      <div>
                        <h3>Управление статусом и ответственным</h3>
                      </div>
                    </header>
                    <div className="owner-uat-panel-body">
                      <div className="owner-uat-grid2">
                        {request.can_edit_status && (
                          <label className="owner-uat-field">
                            <span>Сменить статус</span>
                            <div className="owner-uat-actions">
                              <select
                                value={newStatus}
                                onChange={(e) => setNewStatus(e.target.value)}
                                className="owner-uat-select"
                                style={{ flex: 1 }}
                              >
                                <option value="NOT_STARTED">Не запущена</option>
                                <option value="IN_PROGRESS">В работе</option>
                                <option value="NEEDS_CLARIFICATION">Требует уточнения</option>
                                {request.result && !request.result.is_confirmed_by_client && (
                                  <option value="RESULT_READY">Результат готов · на подтверждение</option>
                                )}
                                <option value="COMPLETED">Завершена</option>
                                <option value="CANCELLED">Отменена</option>
                              </select>
                              <button
                                type="button"
                                disabled={actionLoading || newStatus === request.status || (newStatus === 'NEEDS_CLARIFICATION' && !statusReason.trim())}
                                onClick={handleUpdateStatus}
                                className="owner-uat-btn primary"
                              >
                                Применить
                              </button>
                            </div>
                            {newStatus === 'NEEDS_CLARIFICATION' && (
                              <input
                                type="text"
                                value={statusReason}
                                onChange={(e) => setStatusReason(e.target.value)}
                                placeholder="Укажите причину блокировки / вопроса..."
                                className="owner-uat-input"
                                style={{ marginTop: 8, width: '100%' }}
                              />
                            )}
                          </label>
                        )}

                        {request.can_assign && (
                          <label className="owner-uat-field">
                            <span>Назначить исполнителя</span>
                            <div className="owner-uat-actions">
                              <input
                                type="text"
                                value={newAssignee}
                                onChange={(e) => setNewAssignee(e.target.value)}
                                placeholder="Academia ID, email или полное имя"
                                className="owner-uat-input"
                                style={{ flex: 1 }}
                              />
                              <button
                                type="button"
                                disabled={actionLoading || !newAssignee.trim()}
                                onClick={handleAssign}
                                className="owner-uat-btn"
                              >
                                Назначить
                              </button>
                            </div>
                          </label>
                        )}
                      </div>

                      <div
                        className="owner-uat-actions"
                        style={{ marginTop: 14, justifyContent: 'space-between' }}
                      >
                        <div className="owner-uat-actions">
                          {request.can_edit_status && !request.result && (
                            <button
                              type="button"
                              onClick={() => setShowResultForm(!showResultForm)}
                              className="owner-uat-btn"
                            >
                              + Зафиксировать результат заявки
                            </button>
                          )}
                        </div>
                        {request.can_cancel && (
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={handleCancelRequest}
                            className="owner-uat-btn"
                            style={{ color: '#ff7b65', borderColor: 'rgba(255,123,101,.35)' }}
                          >
                            Отменить заявку
                          </button>
                        )}
                      </div>

                      {showResultForm && (
                        <form onSubmit={handleSubmitResult} className="owner-uat-card" style={{ marginTop: 14, '--kpi': '#49ddb7' } as CSSProperties}>
                          <h3 style={{ color: '#49ddb7', fontSize: 12, textTransform: 'uppercase' }}>
                            Фиксация выполненной работы
                          </h3>
                          <label className="owner-uat-field" style={{ marginTop: 10 }}>
                            <span>Описание итогового результата</span>
                            <textarea
                              rows={3}
                              value={resultSummary}
                              onChange={(e) => setResultSummary(e.target.value)}
                              placeholder="Опишите завершённые этапы, подготовленные документы и рекомендации..."
                              required
                              className="owner-uat-textarea"
                            />
                          </label>
                          <label className="owner-uat-field" style={{ marginTop: 10 }}>
                            <span>Файлы результата</span>
                            <input type="file" multiple onChange={(e) => setSelectedResultFiles(Array.from(e.target.files || []))} className="owner-uat-input" />
                          </label>
                          <div className="owner-uat-actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                            <button
                              type="button"
                              onClick={() => setShowResultForm(false)}
                              className="owner-uat-btn"
                            >
                              Отмена
                            </button>
                            <button type="submit" disabled={resultSubmitting} className="owner-uat-btn primary">
                              {resultSubmitting ? 'Сохранение...' : 'Отправить результат'}
                            </button>
                          </div>
                        </form>
                      )}
                    </div>
                  </section>
                </div>
              )}

              {activeTab === 'details' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-grid2">
                    <article className="owner-uat-card" style={{ '--kpi': '#41d2ff' } as CSSProperties}>
                      <h3 style={{ fontSize: 12, textTransform: 'uppercase', color: '#83a4b4' }}>
                        Параметры сервиса
                      </h3>
                      <div className="owner-uat-kv">
                        <div>
                          <span>Услуга:</span>
                          <strong>{request.service_name}</strong>
                        </div>
                        <div>
                          <span>Направление:</span>
                          <strong>{request.direction}</strong>
                        </div>
                        <div>
                          <span>Тип:</span>
                          <strong>{kindLabel(request.kind)}</strong>
                        </div>
                        <div>
                          <span>Маршрут:</span>
                          <strong>{request.route}</strong>
                        </div>
                        {request.related_project_code && (
                          <div>
                            <span>Связанный проект:</span>
                            <strong style={{ color: '#e2ad3a' }}>
                              {request.related_project_code}
                            </strong>
                          </div>
                        )}
                      </div>
                    </article>

                    <article className="owner-uat-card" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
                      <h3 style={{ fontSize: 12, textTransform: 'uppercase', color: '#83a4b4' }}>
                        Сведения о заявителе
                      </h3>
                      <div className="owner-uat-kv">
                        <div>
                          <span>ФИО заявителя:</span>
                          <strong>{request.requester_name}</strong>
                        </div>
                        <div>
                          <span>Роль в системе:</span>
                          <strong>{roleLabel(request.requester_role)}</strong>
                        </div>
                        <div>
                          <span>Код пользователя:</span>
                          <strong>{request.requester_code || '—'}</strong>
                        </div>
                        <div>
                          <span>Email:</span>
                          <strong>{request.requester_email || '—'}</strong>
                        </div>
                        <div>
                          <span>Дата регистрации:</span>
                          <strong>{request.created_at}</strong>
                        </div>
                      </div>
                    </article>
                  </div>

                  {request.can_view_internal_notes && (
                    <section className="owner-uat-panel">
                      <header>
                        <div>
                          <h3>Внутренние рабочие заметки (владелец / операции)</h3>
                          <p>Не видны конечному заявителю</p>
                        </div>
                        {internalNotesSaved && (
                          <span
                            className="owner-uat-badge"
                            style={{ '--badge': '#49ddb7' } as CSSProperties}
                          >
                            ✓ Заметки сохранены
                          </span>
                        )}
                      </header>
                      <div className="owner-uat-panel-body">
                        <textarea
                          rows={4}
                          value={internalNotesText}
                          onChange={(e) => setInternalNotesText(e.target.value)}
                          placeholder="Координационные пометки, внутренние дедлайны, особенности исполнения..."
                          className="owner-uat-textarea"
                        />
                        <div className="owner-uat-actions" style={{ marginTop: 10, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            disabled={actionLoading}
                            onClick={handleSaveInternalNotes}
                            className="owner-uat-btn"
                          >
                            Сохранить заметки
                          </button>
                        </div>
                      </div>
                    </section>
                  )}
                </div>
              )}

              {activeTab === 'answers' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 16 }}>Ответы опросного листа и исходные материалы</h3>
                    <span style={{ color: '#83a4b4', fontSize: 12 }}>
                      Всего вопросов: {request.answers?.length || 0}
                    </span>
                  </div>

                  {(!request.answers || request.answers.length === 0) && (
                    <div className="owner-uat-note">
                      Опросный лист пуст или не был заполнен при создании заявки.
                    </div>
                  )}

                  {request.answers && request.answers.length > 0 && (
                    <div className="owner-uat-stack">
                      {request.answers.map((ans, idx) => (
                        <article
                          key={idx}
                          className="owner-uat-card"
                          style={{ '--kpi': '#e2ad3a' } as CSSProperties}
                        >
                          <div className="owner-uat-actions" style={{ justifyContent: 'space-between' }}>
                            <strong style={{ color: '#e2ad3a' }}>
                              #{idx + 1}. {ans.question}
                            </strong>
                            {ans.status && (
                              <span
                                className="owner-uat-badge"
                                style={{ '--badge': '#8faebb' } as CSSProperties}
                              >
                                {answerStatusLabel(ans.status)}
                              </span>
                            )}
                          </div>
                          <div className="owner-uat-note" style={{ marginTop: 8, borderLeftColor: '#41d2ff' }}>
                            {typeof ans.answer === 'object' ? <ResultDetails value={ans.answer} /> : String(ans.answer ?? '')}
                          </div>
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'files' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 16 }}>Прикреплённые документы и версии</h3>
                    <button
                      type="button"
                      onClick={() => setShowFileForm(!showFileForm)}
                      className="owner-uat-btn primary"
                    >
                      + Добавить файл / версию
                    </button>
                  </div>

                  {showFileForm && (
                    <form onSubmit={handleAddFile} className="owner-uat-panel">
                      <header>
                        <div>
                          <h3>Регистрация новой версии документа</h3>
                        </div>
                      </header>
                      <div className="owner-uat-panel-body">
                        <div className="owner-uat-grid2">
                          <label className="owner-uat-field">
                            <span>Файл *</span>
                            <input type="file" required onChange={(e) => setSelectedMaterialFile(e.target.files?.[0] || null)} className="owner-uat-input" />
                          </label>
                          <label className="owner-uat-field">
                            <span>Метка версии</span>
                            <input
                              type="text"
                              value={newFileVersion}
                              onChange={(e) => setNewFileVersion(e.target.value)}
                              placeholder="v1, v2, final..."
                              className="owner-uat-input"
                              style={{ width: '100%' }}
                            />
                          </label>
                        </div>
                        <div className="owner-uat-actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                          <button type="button" onClick={() => setShowFileForm(false)} className="owner-uat-btn">
                            Отмена
                          </button>
                          <button type="submit" disabled={fileSubmitting} className="owner-uat-btn primary">
                            {fileSubmitting ? 'Сохранение...' : 'Загрузить файл'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {(!request.files || request.files.length === 0) && (
                    <div className="owner-uat-note">К заявке пока не прикреплено ни одного файла.</div>
                  )}

                  {request.files && request.files.length > 0 && (
                    <div className="owner-uat-file-list">
                      {request.files.map((f) => (
                        <div key={f.id} className="owner-uat-file-row">
                          <div className="owner-uat-actions" style={{ alignItems: 'center' }}>
                            <span
                              className="owner-uat-badge"
                              style={{ '--badge': '#41d2ff' } as CSSProperties}
                            >
                              {f.version}
                            </span>
                            <div>
                              <strong style={{ fontSize: 13 }}>{f.filename}</strong>
                              {f.url && <button type="button" className="owner-uat-btn" onClick={() => void downloadWorkRequestFile(f.url!, f.filename, roleContext).catch((error) => setError(error.message))}>Скачать</button>}
                              <small style={{ display: 'block', color: '#83a4b4', marginTop: 3 }}>
                                Загрузил: {f.uploader_name} ({roleLabel(f.uploader_role)}) · {f.created_at} ·{' '}
                                {f.filesize_label || '—'}
                              </small>
                            </div>
                          </div>
                          <span
                            className="owner-uat-badge"
                            style={{ '--badge': '#8faebb' } as CSSProperties}
                          >
                            {fileStatusLabel(f.status)}
                          </span>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'clarifications' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-actions" style={{ justifyContent: 'space-between', alignItems: 'flex-start' }}>
                    <div>
                      <h3 style={{ margin: 0, fontSize: 16 }}>Уточнения и вопросы по заявке</h3>
                      <p style={{ margin: '4px 0 0', color: '#83a4b4', fontSize: 12 }}>
                        Контроль уточнений с фиксацией дедлайна и ответов
                      </p>
                    </div>
                    {request.can_clarify && (
                      <button
                        type="button"
                        onClick={() => setShowClarificationForm(!showClarificationForm)}
                        className="owner-uat-btn primary"
                      >
                        + Запросить уточнение
                      </button>
                    )}
                  </div>

                  {showClarificationForm && (
                    <form onSubmit={handleCreateClarification} className="owner-uat-panel">
                      <header>
                        <div>
                          <h3 style={{ color: '#e2ad3a' }}>Новый запрос на уточнение</h3>
                        </div>
                      </header>
                      <div className="owner-uat-panel-body">
                        <label className="owner-uat-field">
                          <span>Вопрос или требуемые данные *</span>
                          <textarea
                            rows={3}
                            value={clarificationQuestion}
                            onChange={(e) => setClarificationQuestion(e.target.value)}
                            placeholder="Опишите, какие материалы или пояснения требуются от заказчика..."
                            required
                            className="owner-uat-textarea"
                          />
                        </label>
                        <div className="owner-uat-grid2" style={{ marginTop: 10 }}>
                          <label className="owner-uat-field">
                            <span>Срок ответа (дедлайн)</span>
                            <input
                              type="text"
                              value={clarificationDeadline}
                              onChange={(e) => setClarificationDeadline(e.target.value)}
                              placeholder="Например: До 12.09.2026"
                              className="owner-uat-input"
                              style={{ width: '100%' }}
                            />
                          </label>
                        </div>
                        <div className="owner-uat-actions" style={{ marginTop: 12, justifyContent: 'flex-end' }}>
                          <button
                            type="button"
                            onClick={() => setShowClarificationForm(false)}
                            className="owner-uat-btn"
                          >
                            Отмена
                          </button>
                          <button
                            type="submit"
                            disabled={clarificationSubmitting}
                            className="owner-uat-btn primary"
                          >
                            {clarificationSubmitting ? 'Отправка...' : 'Отправить запрос'}
                          </button>
                        </div>
                      </div>
                    </form>
                  )}

                  {(!request.clarifications || request.clarifications.length === 0) && (
                    <div className="owner-uat-note">Запросов на уточнение по этой заявке нет.</div>
                  )}

                  {request.clarifications && request.clarifications.length > 0 && (
                    <div className="owner-uat-stack">
                      {request.clarifications.map((c) => (
                        <article
                          key={c.id}
                          className="owner-uat-card"
                          style={
                            {
                              '--kpi': c.status === 'PENDING' ? '#e2ad3a' : '#49ddb7',
                            } as CSSProperties
                          }
                        >
                          <div className="owner-uat-actions" style={{ justifyContent: 'space-between' }}>
                            <div className="owner-uat-actions" style={{ alignItems: 'center' }}>
                              <span
                                className="owner-uat-badge"
                                style={
                                  {
                                    '--badge': c.status === 'PENDING' ? '#e2ad3a' : '#49ddb7',
                                  } as CSSProperties
                                }
                              >
                                {c.status === 'PENDING' ? 'Ожидает ответа' : 'Ответ получен'}
                              </span>
                              <span style={{ color: '#83a4b4', fontSize: 12 }}>
                                Запросил: {c.requested_by} · {c.created_at}
                              </span>
                            </div>
                            {c.deadline && (
                              <span style={{ color: '#e2ad3a', fontSize: 12 }}>
                                Срок: {c.deadline}
                              </span>
                            )}
                          </div>

                          <div style={{ marginTop: 10, fontSize: 13 }}>{c.question}</div>

                          {c.answer && (
                            <div className="owner-uat-note ok" style={{ marginTop: 10 }}>
                              <div
                                className="owner-uat-actions"
                                style={{ justifyContent: 'space-between', marginBottom: 4, fontSize: 11 }}
                              >
                                <span>Ответил: {c.answered_by}</span>
                                <span>{c.answered_at}</span>
                              </div>
                              <div style={{ whiteSpace: 'pre-wrap' }}>{c.answer}</div>
                            </div>
                          )}

                          {c.status === 'PENDING' && request.can_answer_clarification && (
                            <div style={{ marginTop: 10 }}>
                              {activeClarificationId === c.id ? (
                                <div className="owner-uat-stack" style={{ gap: 8 }}>
                                  <textarea
                                    rows={3}
                                    value={clarificationAnswerText}
                                    onChange={(e) => setClarificationAnswerText(e.target.value)}
                                    placeholder="Введите ваш ответ и разъяснения по вопросу..."
                                    className="owner-uat-textarea"
                                  />
                                  <div className="owner-uat-actions" style={{ justifyContent: 'flex-end' }}>
                                    <button
                                      type="button"
                                      onClick={() => setActiveClarificationId(null)}
                                      className="owner-uat-btn"
                                    >
                                      Отмена
                                    </button>
                                    <button
                                      type="button"
                                      disabled={answeringSubmitting || !clarificationAnswerText.trim()}
                                      onClick={() => handleAnswerClarification(c.id)}
                                      className="owner-uat-btn primary"
                                    >
                                      {answeringSubmitting ? 'Отправка...' : 'Отправить ответ'}
                                    </button>
                                  </div>
                                </div>
                              ) : (
                                <button
                                  type="button"
                                  onClick={() => {
                                    setActiveClarificationId(c.id);
                                    setClarificationAnswerText('');
                                  }}
                                  className="owner-uat-btn"
                                >
                                  Предоставить ответ
                                </button>
                              )}
                            </div>
                          )}
                        </article>
                      ))}
                    </div>
                  )}
                </div>
              )}

              {activeTab === 'timeline' && (
                <div className="owner-uat-stack">
                  <div className="owner-uat-actions" style={{ justifyContent: 'space-between', alignItems: 'center' }}>
                    <h3 style={{ margin: 0, fontSize: 16 }}>Журнал событий жизненного цикла</h3>
                    <span style={{ color: '#83a4b4', fontSize: 12 }}>
                      Всего событий: {request.timeline?.length || 0}
                    </span>
                  </div>

                  {(!request.timeline || request.timeline.length === 0) && (
                    <div className="owner-uat-note">История событий пока пуста.</div>
                  )}

                  {request.timeline && request.timeline.length > 0 && (
                    <div className="owner-uat-timeline">
                      {request.timeline.map((evt) => (
                        <div key={evt.id} className="owner-uat-timeline-item">
                          <article className="owner-uat-card" style={{ '--kpi': '#e2ad3a' } as CSSProperties}>
                            <div className="owner-uat-actions" style={{ justifyContent: 'space-between' }}>
                              <strong style={{ fontSize: 13 }}>{evt.action}</strong>
                              <span style={{ color: '#83a4b4', fontSize: 11 }}>
                                {evt.timestamp}
                              </span>
                            </div>
                            <div style={{ marginTop: 4, color: '#83a4b4', fontSize: 11 }}>
                              Автор: {evt.actor_name} ({roleLabel(evt.actor_role)})
                            </div>
                            {evt.notes && (
                              <div style={{ marginTop: 6, fontSize: 12 }}>{evt.notes}</div>
                            )}
                          </article>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </>
          )}
        </div>
      </div>
    </div>
  );
};
